import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToastStore } from './useToastStore';

export const ToastContainer: FC = () => {
  const { t } = useTranslation();
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="fixed z-50 flex flex-col gap-2 w-full max-w-sm px-4 top-4 left-1/2 -translate-x-1/2 md:top-auto md:bottom-4 md:right-4 md:left-auto md:translate-x-0"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            layout
            initial={{ opacity: 0, x: 80, y: 0, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, y: 10, transition: { duration: 0.2 } }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className={`relative overflow-hidden flex items-center gap-3 rounded-card px-4 py-3 shadow-card hover:shadow-card-hover transition-all duration-300 ${
              toast.type === 'success' ? 'bg-green-600 text-white' :
              toast.type === 'error' ? 'bg-red-600 text-white' :
              toast.type === 'warning' ? 'bg-amber-500 text-white' :
              'bg-blue-600 text-white'
            }`}
          >
            {toast.type === 'success' && <CheckCircle size={18} className="shrink-0" />}
            {toast.type === 'error' && <AlertCircle size={18} className="shrink-0" />}
            {toast.type === 'warning' && <AlertTriangle size={18} className="shrink-0" />}
            {toast.type === 'info' && <Info size={18} className="shrink-0" />}

            <span className="text-sm font-medium pr-4 break-words flex-1">{toast.message}</span>

            <button
              onClick={() => removeToast(toast.id)}
              className="ml-auto hover:opacity-70 transition-opacity shrink-0 p-1 rounded-md focus-visible:ring-2 focus-visible:ring-white"
              aria-label={t('common.close', 'Close')}
            >
              <X size={16} aria-hidden="true" />
            </button>

            <motion.div
              initial={{ width: '100%' }}
              animate={{ width: 0 }}
              transition={{ duration: toast.duration / 1000, ease: 'linear' }}
              className="absolute bottom-0 left-0 h-[2px] bg-white/40"
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};